import { authRecovery, AuthRecoveryError } from "./authRecovery";
import { ApiError, NetworkError, rawRequest, type ApiOptions } from "./transport";

export { ApiError, normalizeErrorDetail } from "./transport";
export type { ApiOptions } from "./transport";

export interface ApiBlobResponse { blob: Blob; filename: string | null; }

export async function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  return (await apiFetchWithAuthRevision<T>(path, opts)).data;
}

/** me状態の採用には、回復後の再送を含む実際の送信開始版を使う。 */
export async function apiFetchWithAuthRevision<T>(path: string, opts: ApiOptions = {}): Promise<{ data: T; accessRevision: number }> {
  const { response, accessRevision } = await request(path, opts);
  const text = await response.text();
  return { data: text ? JSON.parse(text) as T : undefined as T, accessRevision };
}

export async function apiFetchBlob(path: string, opts: ApiOptions = {}): Promise<ApiBlobResponse> {
  const { response } = await request(path, opts);
  return { blob: await response.blob(), filename: contentDispositionFilename(response.headers.get("Content-Disposition")) };
}

async function request(path: string, opts: ApiOptions): Promise<{ response: Response; accessRevision: number }> {
  const send = async () => {
    const accessRevision = authRecovery.getAccessRevision();
    return { response: await rawRequest(path, opts), accessRevision };
  };
  try {
    return await send();
  } catch (error) {
    const kind = error instanceof ApiError && error.status === 404 && error.detail === "shop_not_installed"
      ? "bootstrap"
      : error instanceof ApiError && error.status === 410 && error.detail === "shop_recovery_required"
        ? "recover" : null;
    if (!kind) {
      if (error instanceof ApiError && error.status === 401) authRecovery.block("unauthorized");
      if (error instanceof ApiError && error.status === 423 && error.detail === "shop_redact_pending") authRecovery.block("redact");
      throw error;
    }
    await authRecovery.ensure(kind);
    // この2コードだけは業務処理に入る前の拒否。元の要求を最大1回再取得する。
    // 課金・発行等の応答喪失や他の404/410からは決してここへ来ない。
    try { return await send(); }
    catch (retryError) {
      if (retryError instanceof ApiError && (retryError.status === 401
        || (retryError.status === 404 && retryError.detail === "shop_not_installed")
        || (retryError.status === 410 && retryError.detail === "shop_recovery_required")
        || (retryError.status === 423 && retryError.detail === "shop_redact_pending"))) {
        authRecovery.block(retryError.status === 423 ? "redact" : "not_ready");
      }
      throw retryError;
    }
  }
}

/** 自動再試行は安全なGETの一時失敗だけ。認証操作と副作用の結果不明は手動確認へ。 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 3 || error instanceof AuthRecoveryError) return false;
  return (error instanceof ApiError || error instanceof NetworkError) && error.retryable;
}

function contentDispositionFilename(value: string | null): string | null {
  return value?.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}
